import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';
import { generateFinancialForecast } from '../../utils/openaiService';

const FinancialInputPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State variables for product data and financial data
  const [productData, setProductData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  
  // Check if financial analysis exists
  const [hasExistingAnalysis, setHasExistingAnalysis] = useState(false);
  
  // Form states
  const [overallProjectRisk, setOverallProjectRisk] = useState('Medium');
  const [discountRate, setDiscountRate] = useState(12);
  const [durationOfExclusivity, setDurationOfExclusivity] = useState(10);
  const [grossProfitMargin, setGrossProfitMargin] = useState(75);
  const [rdCostsToPreclinicalPoc, setRdCostsToPreclinicalPoc] = useState(2500000);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Risk level options
  const riskLevelOptions = ['Low', 'Medium', 'High', 'Very High'];
  
  // Fetch product data and financial data if available
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch product data
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();
        
        if (productError) throw productError;
        setProductData(product);
        
        // Fetch financial data if exists
        const { data: financial, error: financialError } = await supabase
          .from('financial_data')
          .select('*')
          .eq('product_id', id)
          .maybeSingle();
        
        if (!financialError && financial) {
          setFinancialData(financial);
          // Populate form with existing data
          setOverallProjectRisk(financial.overall_project_risk || 'Medium');
          setDiscountRate(financial.discount_rate || 12);
          setDurationOfExclusivity(financial.duration_of_exclusivity || 10);
          setGrossProfitMargin(financial.gross_profit_margin || 75);
          setRdCostsToPreclinicalPoc(financial.rd_costs_to_preclinical_poc || 2500000);
        }
        
        // Check if financial analysis already exists
        const { data: existingAnalysis, error: analysisError } = await supabase
          .from('financial_analysis')
          .select('id')
          .eq('product_id', id)
          .maybeSingle();
        
        if (!analysisError && existingAnalysis) {
          setHasExistingAnalysis(true);
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (user && id) {
      fetchData();
    }
  }, [user, id]);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      setError(null);
      
      // Format data for database
      const financialInputData = {
        product_id: id,
        user_id: user.id,
        overall_project_risk: overallProjectRisk,
        discount_rate: discountRate,
        duration_of_exclusivity: durationOfExclusivity,
        gross_profit_margin: grossProfitMargin,
        rd_costs_to_preclinical_poc: rdCostsToPreclinicalPoc,
        updated_at: new Date().toISOString()
      };
      
      // If we have existing data, include the ID for proper update
      if (financialData?.id) {
        financialInputData.id = financialData.id;
        financialInputData.created_at = financialData.created_at;
      } else {
        financialInputData.created_at = new Date().toISOString();
      }
      
      // Update or insert financial data
      const { error: saveError } = await supabase
        .from('financial_data')
        .upsert(financialInputData, {
          onConflict: 'product_id',
          ignoreDuplicates: false
        });
      
      if (saveError) throw saveError;
      
      // Navigate to pitch deck input page next
      navigate(`/products/${id}/pitchdeck`);
      
    } catch (err) {
      console.error('Error saving financial data:', err);
      setError('Failed to save financial data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle analysis click
  const handleAnalyzeClick = async () => {
    try {
      // Check if analysis already exists - if so, just navigate to it
      if (hasExistingAnalysis) {
        navigate(`/products/${id}/financialanalysis`);
        return;
      }
      
      setIsSaving(true);
      setError(null);
      
      // First save the current form data
      const financialInputData = {
        product_id: id,
        user_id: user.id,
        overall_project_risk: overallProjectRisk,
        discount_rate: discountRate,
        duration_of_exclusivity: durationOfExclusivity,
        gross_profit_margin: grossProfitMargin,
        rd_costs_to_preclinical_poc: rdCostsToPreclinicalPoc,
        updated_at: new Date().toISOString()
      };
      
      // If we have existing data, include the ID for proper update
      if (financialData?.id) {
        financialInputData.id = financialData.id;
        financialInputData.created_at = financialData.created_at;
      } else {
        financialInputData.created_at = new Date().toISOString();
      }
      
      // Save form data to database
      const { error: saveError } = await supabase
        .from('financial_data')
        .upsert(financialInputData, {
          onConflict: 'product_id',
          ignoreDuplicates: false
        });
      
      if (saveError) throw saveError;
      
      // Check if financial_analysis table exists
      try {
        // Try to query the financial_analysis table
        const { error: tableError } = await supabase
          .from('financial_analysis')
          .select('id')
          .limit(1);
        
        // If we get a 404 error, the table doesn't exist
        if (tableError && tableError.code === '42P01') {
          throw new Error('The financial_analysis table does not exist in your database.');
        }
      } catch (tableErr) {
        console.error('Error checking financial_analysis table:', tableErr);
        setError(`The financial_analysis table does not exist in your database. Please create it using the SQL script shown below.`);
        setIsSaving(false);
        return;
      }
      
      // Use OpenAI service for financial analysis
      try {
        const financialParams = {
          overallProjectRisk,
          discountRate,
          durationOfExclusivity,
          grossProfitMargin,
          rdCostsToPreclinicalPoc
        };
        
        // Call the OpenAI service
        const analysisResults = await generateFinancialForecast(productData, financialParams);
        
        // Save analysis results to database
        const { error: analysisError } = await supabase
          .from('financial_analysis')
          .upsert({
            product_id: id,
            user_id: user.id,
            financial_data_id: financialData?.id,
            analysis_results: analysisResults,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'product_id',
            ignoreDuplicates: false
          });
        
        if (analysisError) throw analysisError;
        
        // Navigate to financial analysis results page
        navigate(`/products/${id}/financialanalysis`);
        
      } catch (analysisErr) {
        console.error('Error during financial analysis:', analysisErr);
        
        if (analysisErr.message.includes('OpenAI API key is not configured')) {
          setError('OpenAI API key is not configured. Please add it to your .env.local file.');
        } else {
          setError(`Failed to analyze financial data: ${analysisErr.message}`);
        }
      }
    } catch (err) {
      console.error('Error in financial analysis process:', err);
      setError(`Failed to process financial analysis: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return <LoadingScreen message="Loading financial input page..." />;
  }
  
  if (!productData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>Product not found. Please check the URL and try again.</p>
        </div>
        <Link to="/products" className="btn-primary">
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to={`/products/${id}/analysis`} className="text-primary-600 hover:text-primary-700 flex items-center">
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Back to Analysis
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Financial Input</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6" role="alert">
          <p>{error}</p>
          {error.includes('financial_analysis table does not exist') && (
            <div className="mt-4">
              <p>To create the financial_analysis table, copy the SQL below and run it in your Supabase SQL Editor:</p>
              <div className="mt-2 p-4 bg-gray-800 text-white rounded overflow-auto max-h-96">
                <pre className="text-xs">
{`-- Create the financial_analysis table
CREATE TABLE IF NOT EXISTS public.financial_analysis (
    id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
    product_id UUID NOT NULL REFERENCES public.products(id) ON DELETE CASCADE,
    user_id UUID NOT NULL,
    financial_data_id UUID REFERENCES public.financial_data(id),
    analysis_results JSONB NOT NULL,
    created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
    updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
);

-- Set up Row Level Security
ALTER TABLE public.financial_analysis ENABLE ROW LEVEL SECURITY;

-- Create policy to allow users to view their own financial analyses
CREATE POLICY "Users can view their own financial analyses"
ON public.financial_analysis
FOR SELECT
USING (auth.uid() = user_id);

-- Create policy to allow users to insert their own financial analyses
CREATE POLICY "Users can insert their own financial analyses"
ON public.financial_analysis
FOR INSERT
WITH CHECK (auth.uid() = user_id);

-- Create policy to allow users to update their own financial analyses
CREATE POLICY "Users can update their own financial analyses"
ON public.financial_analysis
FOR UPDATE
USING (auth.uid() = user_id);

-- Add indexes for better performance
CREATE INDEX IF NOT EXISTS financial_analysis_product_id_idx ON public.financial_analysis (product_id);
CREATE INDEX IF NOT EXISTS financial_analysis_user_id_idx ON public.financial_analysis (user_id);
CREATE INDEX IF NOT EXISTS financial_analysis_financial_data_id_idx ON public.financial_analysis (financial_data_id);`}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Financial Parameters</h2>
          <p className="text-gray-600">
            Enter the key financial parameters for {productData.name} to generate financial projections and analyses.
          </p>
          
          {hasExistingAnalysis && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-md">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div>
                  <p className="text-blue-700 font-medium">Financial Analysis Already Exists</p>
                  <p className="text-blue-600 text-sm mt-1">
                    You already have a financial analysis for this product. You can view it by clicking the "View Existing Analysis" button.
                    Modifying these parameters and analyzing again will replace your existing analysis.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="overallProjectRisk" className="block text-gray-700 text-sm font-medium mb-2">
                Overall Project Risk <span className="text-red-500">*</span>
              </label>
              <select
                id="overallProjectRisk"
                value={overallProjectRisk}
                onChange={(e) => setOverallProjectRisk(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                required
              >
                {riskLevelOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <p className="text-gray-500 text-xs mt-1">
                The overall risk level of the project based on technical, regulatory, and market factors.
              </p>
            </div>
            
            <div>
              <label htmlFor="discountRate" className="block text-gray-700 text-sm font-medium mb-2">
                Discount Rate (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="discountRate"
                value={discountRate}
                onChange={(e) => setDiscountRate(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
                step="0.5"
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                The discount rate used for NPV calculations, reflecting the cost of capital and risk.
              </p>
            </div>
            
            <div>
              <label htmlFor="durationOfExclusivity" className="block text-gray-700 text-sm font-medium mb-2">
                Duration of Exclusivity (Years) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="durationOfExclusivity"
                value={durationOfExclusivity}
                onChange={(e) => setDurationOfExclusivity(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                The expected period of market exclusivity (patent protection or regulatory exclusivity).
              </p>
            </div>
            
            <div>
              <label htmlFor="grossProfitMargin" className="block text-gray-700 text-sm font-medium mb-2">
                Gross Profit Margin (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="grossProfitMargin"
                value={grossProfitMargin}
                onChange={(e) => setGrossProfitMargin(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
                max="100"
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                The expected gross profit margin as a percentage of revenue.
              </p>
            </div>
            
            <div>
              <label htmlFor="rdCostsToPreclinicalPoc" className="block text-gray-700 text-sm font-medium mb-2">
                R&D Costs to Preclinical POC ($) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="rdCostsToPreclinicalPoc"
                value={rdCostsToPreclinicalPoc}
                onChange={(e) => setRdCostsToPreclinicalPoc(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                min="0"
                step="100000"
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                The estimated research and development costs to reach preclinical proof of concept.
              </p>
            </div>
      </div>

          <div className="flex justify-end space-x-4">
            {hasExistingAnalysis ? (
              <Link
                to={`/products/${id}/financialanalysis`}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                View Existing Analysis
        </Link>
            ) : (
              <button
                type="button"
                onClick={handleAnalyzeClick}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : "Analyze Financial Data"}
              </button>
            )}
            
            <button
              type="submit"
              className="btn-primary"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : "Save & Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinancialInputPage; 